import { useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ModelResults } from '../features/model/domain/types';
import { getLatestRun } from '../api/projects';

export interface ModelRunPollerOptions {
    onComplete?: (results: ModelResults) => void;
    onError?: (message: string) => void;
    onStatusChange?: (status: 'idle' | 'submitting' | 'running') => void;
}

export function useModelRunPoller(token: string | null, options?: ModelRunPollerOptions) {
    const queryClient = useQueryClient();
    const pollingRef = useRef(false);

    const formatErrorMessage = (error: string): string => {
        try {
            if (error.startsWith("{") && error.endsWith("}")) {
                const obj = JSON.parse(error);
                if (obj.detail) return String(obj.detail);
            }
            return error.replace(/[{}"]/g, "").replace(/detail:/i, "").trim();
        } catch {
            return error.replace(/[{}"]/g, "").replace(/detail:/i, "").trim();
        }
    };

    const pollForResults = useCallback(async (projectId: string, scenarioId?: string | null) => {
        // Prevent duplicate polling loops
        if (pollingRef.current) return null;
        pollingRef.current = true;

        options?.onStatusChange?.('running');

        let consecutiveErrors = 0;
        const cacheKey = ["projectLatestRun", projectId, scenarioId ?? null];

        try {
            for (let attempt = 0; attempt < 30; attempt++) {
                await new Promise((resolve) => setTimeout(resolve, 10_000));

                try {
                    const data = await getLatestRun(projectId, token!, scenarioId);

                    // getLatestRun returns null on non-200 (e.g. 404) — skip and keep polling
                    if (!data) continue;

                    consecutiveErrors = 0; // Reset on successful fetch

                    // Keep cache in sync on every poll (must match query key)
                    queryClient.setQueryData<ModelResults | null>(cacheKey, data);

                    if (data.status === "completed") {
                        pollingRef.current = false;
                        options?.onStatusChange?.('idle');
                        options?.onComplete?.(data);
                        return data;
                    }
                    if (data.status === "failed") throw new Error("Model run failed");
                } catch (error: any) {
                    // "Model run failed" is a definitive status — abort immediately
                    if (error.message === "Model run failed") throw error;

                    // Transient network/fetch errors — tolerate up to 3 consecutive failures
                    consecutiveErrors++;
                    if (consecutiveErrors >= 3) throw error;
                    // Otherwise, continue polling
                }
            }
            throw new Error("Timeout waiting for model run");
        } catch (err: any) {
            const friendlyMessage = formatErrorMessage(err.message ?? String(err));
            pollingRef.current = false;
            options?.onStatusChange?.('idle');
            options?.onError?.(friendlyMessage);
            return null;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, queryClient]);

    return {
        isPolling: pollingRef.current,
        pollingRef,
        pollForResults
    };
}
